import {
  Box,
  Button,
  Flex,
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  VisuallyHidden,
  useBoolean,
} from '@chakra-ui/react';
import { TEXT_EDITED } from 'constants/mixpanel';
import { useDebouncedCallback } from 'hooks/useDebounce';
import { useOutsideClick } from 'hooks/useOutsideClick';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { autoBrandModalsIds } from 'modules/Editor/components/AutoBrand/AutoBrand.config';
import { ColorPicker } from 'modules/Editor/components/ColorPicker';
import { SliderNumberInput } from 'modules/common/components/Input/SliderNumberInput';
import { ReactElement, ReactNode, memo, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useWidget } from 'widgets/sdk';

import { Effect, EffectType, TextWidgetData } from '../../TextWidget.types';
import { DEFAULT_EFFECT } from '../../defaultTextData';
import { MAX_DIRECTION, MIN_DIRECTION, effects, sliderWidth } from './Effects.config';
import {
  EffectButtonProps,
  EffectSelectProps,
  EffectStyleProps,
  EffectsProps,
  PrimaryColorProps,
  SliderProps,
} from './Effects.types';

const EffectButton = memo(({ isActive, label, labelStyle, onClick }: EffectButtonProps) => (
  <Box
    flexGrow={1}
    borderRadius='base'
    p={1}
    background={isActive ? 'hover.blue' : ''}
    onClick={onClick}
    as='button'
    transition='background 0.1s linear'
    aria-label={label}
    _hover={{ background: isActive ? '' : 'black-alpha.200' }}
  >
    <Flex
      borderRadius='base'
      p='0.375rem 1.25rem'
      border='1px solid'
      borderColor={isActive ? 'upgrade.700' : 'gray.200'}
      align='center'
      justify='center'
      fontSize='2rem'
      lineHeight='1.75rem'
      fontWeight='700'
      background='white'
      color='font.500'
      style={labelStyle}
    >
      Aa
    </Flex>
    <Text fontSize='sm' color={isActive ? 'upgrade.700' : 'font.500'} fontWeight='600' mt={1}>
      {label}
    </Text>
  </Box>
));
EffectButton.displayName = 'EffectButton';

const EffectSelect = ({ getTextColor, onTrack }: EffectSelectProps) => {
  const { effect: { type = '' } = { type: '' }, updateWidget } = useWidget<TextWidgetData>();

  const onSetEffect = (type: EffectType, defaultValues: Partial<Effect>, isSameAsTextColor: boolean) => {
    const primaryColor = isSameAsTextColor ? getTextColor() : defaultValues.primaryColor;
    updateWidget({ effect: { type, ...defaultValues, primaryColor } });
  };

  return (
    <>
      {effects.map((effect) => {
        const onClickEffect = () => {
          onSetEffect(effect.type as EffectType, effect.defaultValues || {}, !!effect.isSameAsTextColor);

          const styling_value = TEXT_EDITED.styling_value[effect.label as keyof typeof TEXT_EDITED.styling_value];
          if (!styling_value) console.warn(`Missing tracking event for ${effect.label}`);
          else onTrack(styling_value);
        };

        return (
          <EffectButton
            key={effect.type}
            onClick={onClickEffect}
            labelStyle={effect.labelStyle}
            label={effect.label}
            isActive={type === effect.type}
          />
        );
      })}
    </>
  );
};

const Offset = ({ onTrack }: SliderProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { offset } = { offset: DEFAULT_EFFECT.offset }, updateWidget } = useWidget<TextWidgetData>();
  const onSetOffset = (offset: number) => {
    updateWidget({ effect: { offset } });
    onTrack(TEXT_EDITED.styling_value.Offset);
  };

  return (
    <SliderNumberInput
      fontSize='sm'
      title={t('effects.offsetLabel')}
      fontWeight='normal'
      value={offset}
      onChange={onSetOffset}
      sliderWidth={sliderWidth}
    />
  );
};

const Direction = ({ onTrack }: SliderProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { direction } = { direction: DEFAULT_EFFECT.direction }, updateWidget } = useWidget<TextWidgetData>();
  const onSetDirection = (direction: number) => {
    updateWidget({ effect: { direction } });
    onTrack(TEXT_EDITED.styling_value.Direction);
  };

  return (
    <SliderNumberInput
      fontSize='sm'
      fontWeight='normal'
      title={t('effects.directionLabel')}
      value={direction}
      onChange={onSetDirection}
      sliderWidth={sliderWidth}
      maxValue={MAX_DIRECTION}
      minValue={MIN_DIRECTION}
    />
  );
};

const Blur = ({ onTrack }: SliderProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { blur } = { blur: DEFAULT_EFFECT.blur }, updateWidget } = useWidget<TextWidgetData>();
  const onSetBlur = (blur: number) => {
    updateWidget({ effect: { blur } });
    onTrack(TEXT_EDITED.styling_value.Blur);
  };

  return (
    <SliderNumberInput
      fontSize='sm'
      fontWeight='normal'
      title={t('effects.blurLabel')}
      value={blur}
      onChange={onSetBlur}
      sliderWidth={sliderWidth}
    />
  );
};

const Transparency = ({ onTrack }: SliderProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { transparency } = { transparency: DEFAULT_EFFECT.transparency }, updateWidget } =
    useWidget<TextWidgetData>();
  const onSetTransparency = (transparency: number) => {
    updateWidget({ effect: { transparency } });
    onTrack(TEXT_EDITED.styling_value.Transparency);
  };

  return (
    <SliderNumberInput
      fontSize='sm'
      fontWeight='normal'
      title={t('effects.transparencyLabel')}
      value={transparency}
      onChange={onSetTransparency}
      sliderWidth={sliderWidth}
    />
  );
};

const PrimaryColor = ({ label }: PrimaryColorProps) => {
  const { effect: { primaryColor } = { primaryColor: DEFAULT_EFFECT.primaryColor }, updateWidget } =
    useWidget<TextWidgetData>();

  const onSetPrimaryColor = (primaryColor: string) => updateWidget({ effect: { primaryColor } });

  return (
    <Flex align='center' w='full' justify='space-between'>
      <Text fontSize='sm' color='font-500' fontWeight='normal'>
        {label}
      </Text>
      <ColorPicker
        label={label}
        color={primaryColor}
        onChange={onSetPrimaryColor}
        showNoColorOption={false}
        showNoColorChecker
        showOpacityOption={false}
        placement='right-start'
      />
    </Flex>
  );
};

const Intensity = ({ onTrack }: SliderProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { intensity } = { intensity: DEFAULT_EFFECT.intensity }, updateWidget } = useWidget<TextWidgetData>();

  const onSetIntensity = (intensity: number) => {
    updateWidget({ effect: { intensity } });
    onTrack(TEXT_EDITED.styling_value.Intensity);
  };

  return (
    <SliderNumberInput
      fontSize='sm'
      fontWeight='normal'
      title={t('effects.intensityLabel')}
      value={intensity}
      onChange={onSetIntensity}
      sliderWidth={sliderWidth}
    />
  );
};

const Thickness = ({ onTrack }: SliderProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { thickness } = { thickness: DEFAULT_EFFECT.thickness }, updateWidget } = useWidget<TextWidgetData>();
  const onSetThickness = (thickness: number) => {
    updateWidget({ effect: { thickness } });
    onTrack(TEXT_EDITED.styling_value.Thickness);
  };

  return (
    <SliderNumberInput
      fontSize='sm'
      fontWeight='normal'
      title={t('effects.thicknessLabel')}
      value={thickness}
      onChange={onSetThickness}
      sliderWidth={sliderWidth}
      minValue={1}
    />
  );
};

const EffectStyle = ({ onTrack }: EffectStyleProps) => {
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const { effect: { type } = { type: DEFAULT_EFFECT.type } } = useWidget<TextWidgetData>();

  return (
    <>
      {(type === 'shadow' || type === 'offset') && (
        <>
          <Offset onTrack={onTrack} />
          <Direction onTrack={onTrack} />
          <Blur onTrack={onTrack} />
          <Transparency onTrack={onTrack} />
          <PrimaryColor label={t('effects.shadowColorLabel')} />
        </>
      )}
      {type === 'glow' && (
        <>
          <Intensity onTrack={onTrack} />
          <PrimaryColor label={t('effects.shadowColorLabel')} />
        </>
      )}
      {type === 'outline' && (
        <>
          <Thickness onTrack={onTrack} />
        </>
      )}
      {type === 'repeat' && (
        <>
          <Offset onTrack={onTrack} />
          <Direction onTrack={onTrack} />
          <PrimaryColor label={t('effects.offsetColorLabel')} />
        </>
      )}
    </>
  );
};

interface EffectsContentContainerProps {
  containerId: string | string[];
  children: ReactNode;
  closePopover: () => void;
}

/**
 * Component to handle clicking outside the popover
 * Add a container id to avoid closing the modal when clicking outside
 * @returns ReactNode
 */
const EffectsContentContainer = ({ children, closePopover, containerId }: EffectsContentContainerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick({ ref: containerRef, handler: closePopover, ignoreId: containerId });

  return (
    <Box ref={containerRef} data-testid='text-widget-effect-menu-trigger-container'>
      {children}
    </Box>
  );
};

export const Effects = memo(({ getTextColor }: EffectsProps): ReactElement => {
  const [isOpen, setIsOpen] = useBoolean(false);
  const { effect: { type = '' } = { type: '' } } = useWidget<TextWidgetData>();
  const { t } = useTranslation('editor_widget_toolbar', { keyPrefix: 'textToolbar', useSuspense: false });
  const containerId = useId();

  const label = t('effects.label');
  const hasEffect = !!type;
  const isButtonActive = isOpen || hasEffect;

  // Debounced because it's called when the slider is dragged
  const onTrack = useDebouncedCallback((styling_value: string) => {
    Mixpanel.track(TEXT_EDITED.eventName, { styling_property: TEXT_EDITED.styling_property.Effect, styling_value });
  }, 200);

  return (
    <Popover isOpen={isOpen} isLazy onClose={setIsOpen.off} placement='bottom-start' closeOnBlur={false}>
      <PopoverTrigger>
        <Button
          variant='ghost-with-active'
          data-testid='text-widget-effect-menu-trigger-button'
          fontWeight='semibold'
          onClick={setIsOpen.toggle}
          isActive={isButtonActive}
          size='sm'
        >
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent w={80} boxShadow='md' padding={0} data-testid='text-widget-effect-menu-content'>
        <VisuallyHidden>
          <PopoverHeader>{label}</PopoverHeader>
        </VisuallyHidden>
        <EffectsContentContainer closePopover={setIsOpen.off} containerId={[containerId, ...autoBrandModalsIds]}>
          <Flex pt={2} pb={hasEffect ? 0 : 2} px={2} flexWrap='wrap' gap='0.375rem' rowGap='0.625rem'>
            <EffectSelect onTrack={onTrack} getTextColor={getTextColor} />
          </Flex>
          {hasEffect && (
            <>
              <Flex my='0.625rem' w='full' h='1px' background='gray.200' />
              <Flex pb={2} px={2} rowGap='0.625rem' direction='column'>
                <EffectStyle onTrack={onTrack} />
              </Flex>
            </>
          )}
        </EffectsContentContainer>
      </PopoverContent>
    </Popover>
  );
});
Effects.displayName = 'Effects';
